'use client'

import { useEffect, useRef, useState } from 'react'
import { LAB_EXAMPLES } from '../_lessons/exercises/lab-examples'
import { progress, useHydrated, useProgress } from '../_lib/progress-client'
import GdHeader from './GdHeader'
import { IconPlay, IconReset, IconStop } from './icons'
import CodeEditor, { type EditorHandle } from './ide/CodeEditor'
import Output from './ide/Output'
import { useRunner } from './ide/useRunner'

const LAB = 'lab'
const LAST_KEY = 'gd:lab:last'

function tidy(code: string) {
  return code.replace(/^\n+/, '').replace(/\s+$/, '') + '\n'
}

export default function LabView() {
  const hydrated = useHydrated()
  const state = useProgress()
  const [active, setActive] = useState('scratch')
  const editor = useRef<EditorHandle>(null)
  const runner = useRunner(editor)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const ex = LAB_EXAMPLES.find(e => e.id === active) ?? LAB_EXAMPLES[0]
  const saved = state.code[`${LAB}/${ex.id}`]?.s
  const initial = saved || tidy(ex.code)

  useEffect(() => {
    let last: string | null = null
    try {
      last = localStorage.getItem(LAST_KEY)
    } catch {}
    if (last && LAB_EXAMPLES.some(e => e.id === last)) {
      const t = setTimeout(() => setActive(last as string), 0)
      return () => clearTimeout(t)
    }
  }, [])

  function pick(id: string) {
    if (id === active) return
    runner.stop()
    runner.clear()
    setActive(id)
    try {
      localStorage.setItem(LAST_KEY, id)
    } catch {}
  }

  function onChange(v: string) {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    const id = ex.id
    saveTimer.current = setTimeout(() => progress.saveCode(LAB, id, v === tidy(ex.code) ? '' : v), 700)
  }

  function reset() {
    editor.current?.setValue(tidy(ex.code))
    progress.saveCode(LAB, ex.id, '')
    runner.clear()
  }

  const busy = runner.state !== 'idle'
  const run = () => runner.start({ timeoutMs: null })

  return (
    <div className="flex min-h-screen flex-col">
      <GdHeader center={<p className="gd-label hidden text-center md:block">The Lab — a free-form playground</p>} />
      <main id="main-content" className="flex flex-1 flex-col lg:flex-row" style={{ minHeight: 'calc(100vh - 60px)' }}>
        <aside className="shrink-0 border-b p-4 lg:w-[260px] lg:border-b-0 lg:border-r" style={{ borderColor: 'var(--gd-rule)' }}>
          <p className="gd-label mb-3">Examples</p>
          <nav className="flex gap-1.5 overflow-x-auto lg:flex-col" aria-label="Lab examples">
            {LAB_EXAMPLES.map(e => {
              const edited = Boolean(state.code[`${LAB}/${e.id}`]?.s)
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => pick(e.id)}
                  className="shrink-0 rounded-[10px] px-3 py-2.5 text-left transition-colors lg:shrink"
                  style={{ background: e.id === active ? 'var(--gd-surface)' : 'transparent', border: `1px solid ${e.id === active ? 'var(--gd-rule-2)' : 'transparent'}` }}
                >
                  <span className="flex items-center gap-2 text-[14px] font-semibold">
                    {e.title}
                    {hydrated && edited && <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--gd-accent)' }} title="You’ve edited this" />}
                  </span>
                  <span className="mt-0.5 hidden text-[12.5px] leading-snug lg:block" style={{ color: 'var(--gd-ink-3)' }}>
                    {e.blurb}
                  </span>
                </button>
              )
            })}
          </nav>
          <p className="mt-6 hidden text-[12.5px] leading-relaxed lg:block" style={{ color: 'var(--gd-ink-3)' }}>
            Your edits save automatically and sync with your progress. There’s no time limit here — use Stop for long runs.
          </p>
        </aside>

        <section className="flex min-w-0 flex-1 flex-col xl:flex-row">
          <div className="flex min-h-[420px] min-w-0 flex-1 flex-col border-b xl:border-b-0 xl:border-r" style={{ borderColor: 'var(--gd-rule)' }}>
            <div className="gd-ide-bar !rounded-none">
              <span className="mr-auto truncate text-[14px] font-semibold">{ex.title}</span>
              <button type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={reset} disabled={busy}>
                <IconReset size={13} /> Reset
              </button>
              {busy ? (
                <button type="button" className="gd-btn gd-btn-ink gd-btn-xs" onClick={runner.stop}>
                  <IconStop size={12} /> Stop
                </button>
              ) : (
                <button type="button" className="gd-btn gd-btn-primary gd-btn-xs" onClick={run} title="Run (⌘/Ctrl + Enter)">
                  <IconPlay size={12} /> Run
                </button>
              )}
            </div>
            <div className="relative flex-1" style={{ background: 'var(--gd-code-bg)' }}>
              {hydrated ? (
                <CodeEditor key={ex.id} ref={editor} path={`lab/${ex.id}.ts`} initialValue={initial} onChange={onChange} onRun={run} fixedHeight="100%" />
              ) : null}
            </div>
          </div>
          <div className="flex min-h-[300px] min-w-0 flex-col xl:w-[44%]">
            <div className="gd-ide-bar !rounded-none">
              <span className="gd-label mr-auto">Output</span>
              <button type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={runner.clear} disabled={busy}>
                Clear
              </button>
            </div>
            <div className="relative flex-1">
              <div className="absolute inset-0 overflow-auto">
                <Output items={runner.items} state={runner.state} result={runner.result} maxHeight="none" />
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
